import { useEffect, useMemo, useState } from "react";
import LoadingButton from "@mui/lab/LoadingButton";
import { useTheme } from "@mui/material/styles";
import { greenButtonSx } from "../actionStyles";
import { goalsProgress } from "../../../lib/meter";
import TodayIcon from "@mui/icons-material/Today";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { IconButton, Tooltip } from "@mui/material";
import MyGoalsDrawer from "../../goals/MyGoalsDrawer";
import Card from "../../../components/ui/Card";
import ProgressRing from "../../../components/ui/ProgressRing";
import GoalValueControl from "../../results/components/GoalValueControl";
import { useScriptMode } from "../../../contexts/ScriptModeContext";
import { useAuth } from "../../../contexts/AuthContext";
import { useToast } from "../../../components/ui/Toast";
import { useUpsertMyLogEntryMutation } from "../../../api/dailyLogApi";
import { useGetMyEditWindowQuery } from "../../../api/userApi";
import { isEditableDay, normalizeEditWindow } from "../../../lib/editWindow";
import {
  dayGoalsFor,
  dayLogRows,
  goalValueIn,
  isDayPerfect,
  isGoalDone,
  savedDayValues,
  visibleDayGoals,
} from "../../../lib/dayGoals";
import { ACTIVITY_LABEL_KEYS, goalKey, unitWord } from "../../goals/goalRules";
import {
  formatTurkishDate,
  getTimezone,
  getTimezoneOffset,
  localDateString,
  parseLocalDate,
} from "../../../lib/dateUtils";

// Değerler ad + biçim anahtarlı (aynı ad iki biçimde ayrı hedef).
function initialValues(goals, entry) {
  return savedDayValues(goals, entry?.log?.dailyLogs);
}

// Hedef çubuğunun rengi artışa göre değişiyor: düşük ilerleme uyarı tonunda,
// hedef dolduğunda marka renginde.
function barTone(percent) {
  // Koyu temada dolgu beyaz (mobille aynı).
  if (percent >= 100) return "bg-terakkiPrimaryDark dark:!bg-white";
  if (percent >= 67) return "bg-terakkiPrimary dark:!bg-white";
  if (percent >= 34) return "bg-terakkiWarning dark:!bg-white";
  if (percent > 0) return "bg-terakkiError dark:!bg-white";
  return "bg-transparent";
}

function completionOf(goals, values) {
  const counted = goals.filter((g) => (g.goalValue || 0) > 0);
  if (counted.length === 0) return 0;
  const total = counted.reduce(
    (sum, g) => sum + Math.min(100, (goalValueIn(values, g) / g.goalValue) * 100),
    0
  );
  return Math.round(total / counted.length);
}

export default function DayEditorColumn({ localDate, entry, loading = false, onSaved, onProgressChange }) {
  const { t } = useScriptMode();
  const { user } = useAuth();
  const toast = useToast();

  // O günün snapshot'ı (yoksa profil); birim ve biçim eski gün kayıtlarında
  // yok, güncel hedeften tamamlanır.
  const allGoals = useMemo(() => dayGoalsFor(entry, user?.goalDetail), [entry, user]);
  const [values, setValues] = useState(() => initialValues(allGoals, entry));

  // Gün değişince ya da sunucudan yeni anlık görüntü gelince formu tazele.
  useEffect(() => {
    setValues(initialValues(allGoals, entry));
  }, [localDate, entry, allGoals]);

  const [upsertLog, { isLoading: saving }] = useUpsertMyLogEntryMutation();
  const [goalsOpen, setGoalsOpen] = useState(false);
  const mode = useTheme().palette.mode;

  // Haftanın belli günlerine kurulmuş hedef diğer günlerde görünmez (mobil
  // useAmelAgenda gibi); o gün değer girilmişse görünmeye devam eder.
  const savedValues = useMemo(() => initialValues(allGoals, entry), [allGoals, entry]);
  const goals = useMemo(() => visibleDayGoals(allGoals, localDate, savedValues), [allGoals, localDate, savedValues]);

  // Günün metresi anlık değerlerle dolsun (kaydetmeden önce de).
  const liveProgress = goalsProgress(goals, values);
  useEffect(() => {
    if (onProgressChange) onProgressChange(liveProgress);
  }, [liveProgress, onProgressChange]);

  const percent = completionOf(goals, values);
  // Kaydedilmiş değerlerle birebir karşılaştırıyoruz; yüzde üzerinden bakmak
  // yanıltıcıydı (farklı değerler aynı ortalamayı verebiliyor).
  const dirty = goals.some((g) => goalValueIn(values, g) !== goalValueIn(savedValues, g));
  // Hedefi 0 olan hedef yapılmış sayılmaz, mükemmel günü de engellemez.
  const isPerfect = isDayPerfect(goals, values);
  const streak = entry?.dayTotals?.leftStreak || 0;
  const dateObj = parseLocalDate(localDate);
  const today = localDateString(new Date());
  const isFuture = localDate > today;
  // Geçmiş gün sınırı gruplardan gelir (en esnek olanı); yüklenene dek 7 gün.
  const { data: editWindowData } = useGetMyEditWindowQuery(undefined, { refetchOnMountOrArgChange: 60 });
  const editWindowDays = normalizeEditWindow(editWindowData?.data?.days);
  const isLockedPast = !isFuture && !isEditableDay(localDate, today, editWindowDays);

  const handleSave = async () => {
    const result = await upsertLog({
      // Yalnız o gün geçerli (ya da değeri olan) hedefler gider; geçerli
      // olmayan hedef 0 satırı olarak kaydedilmez.
      dailyLogs: dayLogRows(goals, values),
      localDate,
      timezone: getTimezone(),
      timezoneOffset: getTimezoneOffset(),
    });
    if (result.error) {
      toast.error(result.error.message || t("results_save_failed"));
      return;
    }
    toast.success(t("results_save_success"));
    if (onSaved) onSaved();
  };

  return (
    <Card
      data-tour="goals"
      className="!p-0 overflow-hidden motion-safe:animate-rise-in [animation-delay:.18s] flex flex-col min-h-[320px] max-h-[calc(100vh-3rem)] lg:h-full lg:max-h-full lg:min-h-0"
    >
      {/* Renkli başlık şeridi — hatim kutusuyla aynı dil */}
      <div className="shrink-0 flex items-center gap-2.5 px-4 py-3 bg-terakkiBrandCloud border-b border-terakkiBrandMist">
        <span className="w-8 h-8 rounded-lg bg-terakkiPrimaryDark text-white flex items-center justify-center shrink-0">
          <TodayIcon sx={{ fontSize: 18 }} aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[13px] uppercase tracking-wide text-terakkiNeutral font-SemiBold leading-tight">
            {t("profile_my_goals")}
          </div>
          <div className="text-base font-SemiBold text-terakkiPrimaryDark leading-tight truncate">
            {dateObj ? formatTurkishDate(dateObj) : localDate}
          </div>
        </div>
        {/* Hedef ekleme, düzenleme ve silme çekmecede (sol menüdeki sayfanın yerine) */}
        <Tooltip title={t("my_goals_edit")}>
          <IconButton
            size="small"
            onClick={() => setGoalsOpen(true)}
            aria-label={t("my_goals_edit")}
            data-edit-goals
            className="!bg-white/70 hover:!bg-white dark:!bg-white/10 dark:hover:!bg-white/20"
          >
            <EditOutlinedIcon sx={{ fontSize: 18 }} className="text-terakkiPrimaryDark dark:text-white" />
          </IconButton>
        </Tooltip>
      </div>

      <div className="p-4 flex flex-col gap-4 flex-1 min-h-0">
        {loading ? (
          // Ay değişirken: halka ve hedef satırlarının iskeleti (takvimle aynı anda).
          <div className="flex flex-col gap-4 flex-1 min-h-0" data-testid="goals-skeleton">
            <div className="flex items-center gap-4">
              <span className="w-[84px] h-[84px] rounded-full bg-terakkiSurfaceAlt animate-pulse shrink-0" />
              <div className="flex flex-col gap-2 flex-1">
                <span className="h-4 w-1/2 rounded bg-terakkiSurfaceAlt animate-pulse" />
                <span className="h-4 w-1/3 rounded bg-terakkiSurfaceAlt animate-pulse" />
              </div>
            </div>
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="flex flex-col gap-2 py-1">
                <div className="flex justify-between gap-3">
                  <span className="h-4 w-2/5 rounded bg-terakkiSurfaceAlt animate-pulse" />
                  <span className="h-4 w-12 rounded bg-terakkiSurfaceAlt animate-pulse" />
                </div>
                <span className="h-8 w-full rounded-lg bg-terakkiSurfaceAlt animate-pulse" />
              </div>
            ))}
          </div>
        ) : (
        <>
        <div className="flex items-center gap-4">
          <ProgressRing value={percent} size={84} stroke={8} color="#48A6A7" fillClassName="dark:stroke-white" />
          <div className="flex flex-col gap-1 text-base">
            {isPerfect && goals.length > 0 && (
              <span className="text-terakkiPrimaryDark font-SemiBold">★ {t("calendar_perfect_day")}</span>
            )}
            {streak > 0 && (
              <span className="text-terakkiNeutral">
                {t("calendar_chain")}: <strong>{streak}</strong> {t("calendar_chain_unit")}
              </span>
            )}
            <span className="text-terakkiNeutral">
              {goals.filter((g) => isGoalDone(g, goalValueIn(values, g))).length} / {goals.length}{" "}
              {t("calendar_goals_done")}
            </span>
          </div>
        </div>

        {goals.length === 0 ? (
          <p className="text-base text-terakkiNeutral flex-1">{t("results_no_goals")}</p>
        ) : (
          <div className="flex flex-col divide-y divide-terakkiBorder flex-1 min-h-0 overflow-y-auto pr-1">
            {goals.map((goal, index) => {
              const key = goalKey(goal.label, goal.activity);
              const value = goalValueIn(values, goal);
              const target = goal.goalValue || 0;
              const done = isGoalDone(goal, value);
              const goalPercent = target > 0 ? Math.min(100, Math.round((value / target) * 100)) : 0;
              return (
                <div
                  key={key}
                  className="py-2.5 flex flex-col gap-1.5 motion-safe:animate-rise-in"
                  style={{ animationDelay: `${120 + Math.min(index, 12) * 60}ms` }}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="flex items-center gap-1.5 min-w-0">
                      <span
                        className={`text-base font-SemiBold truncate ${done ? "text-terakkiPrimaryDark" : "text-terakkiInk"}`}
                      >
                        {done && <span aria-hidden>✓ </span>}
                        {goal.label}
                      </span>
                      {goal.activity && ACTIVITY_LABEL_KEYS[goal.activity] ? (
                        <span className="shrink-0 text-[13px] px-1.5 py-px rounded-full bg-terakkiBrandCloud text-terakkiPrimaryDark">
                          {t(ACTIVITY_LABEL_KEYS[goal.activity])}
                        </span>
                      ) : null}
                    </span>
                    <span className="text-[13px] text-terakkiNeutral whitespace-nowrap">
                      {value} / {target}
                      {goal.unit ? ` ${unitWord(goal.unit, t)}` : ""}
                    </span>
                  </div>
                  <GoalValueControl
                    value={value}
                    onChange={(next) => setValues((prev) => ({ ...prev, [key]: next }))}
                    onComplete={
                      target > 0
                        ? () => setValues((prev) => ({ ...prev, [key]: target }))
                        : undefined
                    }
                  />
                  <div
                    className="h-1.5 rounded-full bg-terakkiSurfaceAlt overflow-hidden"
                    role="progressbar"
                    aria-valuenow={goalPercent}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label={goal.label}
                  >
                    <div
                      data-goal-bar={key}
                      className={`h-full rounded-full transition-[width,background-color] duration-200 ${barTone(goalPercent)}`}
                      style={{ width: `${goalPercent}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        </>
        )}

        <LoadingButton
          className="shrink-0"
          variant="contained"
          loading={saving}
          disabled={loading || goals.length === 0 || isFuture || isLockedPast || !dirty}
          onClick={handleSave}
          fullWidth
          sx={greenButtonSx(mode)}
        >
          {dirty ? t("results_save") : t("calendar_saved")}
        </LoadingButton>
        {isLockedPast && !loading ? (
          <p className="shrink-0 -mt-2 text-sm text-center text-terakkiNeutral dark:text-white/70" data-testid="edit-window-note">
            {editWindowDays === 0
              ? t("edit_window_locked_today")
              : t("edit_window_locked", { days: editWindowDays })}
          </p>
        ) : null}
      </div>

      <MyGoalsDrawer open={goalsOpen} onClose={() => setGoalsOpen(false)} />
    </Card>
  );
}
